import { api } from './client';
import type { CEFRLevel } from './courses';
import { useAuthStore } from '@/stores/auth';

/** One meaning of a word. English words are often polysemous — "patient" is a
 * noun (пациент) + adjective (терпеливый); "live" is a verb /lɪv/ (жить) +
 * adjective /laɪv/ (живой). Each sense carries its own POS, optional per-sense
 * IPA, translations, a simple definition and an example using the word in it. */
export type WordSense = {
  pos?: string;
  ipa?: string;
  translation_ru?: string;
  translation_kk?: string;
  definition?: string;
  example?: string;
  example_ru?: string;
};

/** A word's inflected forms — only the fields relevant to its part of speech
 * are filled (verb → past/-ing/3rd-person; noun → plural; adj → comparative). */
export type WordForms = {
  base?: string;
  past?: string;
  past_participle?: string;
  present_participle?: string;
  third_person?: string;
  plural?: string;
  comparative?: string;
  superlative?: string;
};

export type VocabularyWord = {
  id: string;
  word: string;
  translation_kz?: string;
  translation_ru?: string;
  explanation?: string;
  example?: string;
  example_translation?: string;
  audio_url?: string;
  cefr_level?: CEFRLevel;
  // Dictionary data — every distinct meaning + inflected forms (AI-generated).
  senses?: WordSense[];
  forms?: WordForms;
  created_at?: string;
};

export type UserVocabEntry = {
  id: string;
  user_id: string;
  word_id: string;
  word: VocabularyWord;
  is_favorite: boolean;
  is_learned: boolean;
  // Spaced-repetition fields
  next_review_at?: string;
  review_count?: number;
  ease_factor?: number;
};

export type VocabStats = {
  total: number;
  learned: number;
  /** Начатые, но ещё не осевшие в памяти. */
  learning: number;
  /** Ни разу не тренировались. */
  fresh: number;
  /**
   * Засчитано по каждому способу: способ → сколько слов сдано этим способом.
   *
   * ⚠️ БЕЗ ЭТОГО РАЗДЕЛЕНИЕ СПОСОБОВ РАБОТАЕТ ВСЛЕПУЮ. Ученик жмёт «Составить
   * предложение» и не знает, каким словам это ещё нужно, а каким нет.
   *
   * ⚠️ ПОРОГ — ОДИН ВЕРНЫЙ ОТВЕТ, тот же, что у «Выучено» (сервер,
   * `model.ModeCountedBox`, решение владельца 10.09.2026). До этого порогом была
   * последняя ступень SM-2, и ученик, ответивший в «на слух» пятьдесят два раза,
   * видел на этой кнопке `0` — именно это и читалось как поломка.
   */
  modes: Record<string, number>;
  favorites: number;
  due_today: number;
};

/**
 * Backend raw shapes (after client.ts envelope unwrap):
 *  - model.VocabularyWord:    { id, word, translation, explanation, example, example_trans, audio_url, level, created_at }
 *  - model.StudentVocabulary: { id, student_id, word_id, is_learned, is_favourite, notes, created_at, updated_at }
 *  - model.SRSEntry:          { id, student_id, word_id, box, interval_days, easiness, repetitions, last_review, next_review, ... }
 * None of the student list endpoints join the VocabularyWord, so we build a
 * best-effort `word` object from whatever fields are present to satisfy the
 * frontend contract and avoid `entry.word.*` crashes in the page.
 */

type RawVocabWord = {
  id?: string;
  word?: string;
  translation?: string;
  explanation?: string;
  example?: string;
  example_trans?: string;
  audio_url?: string;
  level?: string;
  senses?: WordSense[];
  forms?: WordForms;
  created_at?: string;
};

type RawStudentVocab = {
  id?: string;
  student_id?: string;
  word_id?: string;
  is_learned?: boolean;
  is_favourite?: boolean;
  notes?: string;
  created_at?: string;
  // Backend GET /vocab/student/words now joins the dictionary word under `word`.
  word?: RawVocabWord;
  vocabulary_word?: RawVocabWord;
};

type RawSRSEntry = {
  id?: string;
  student_id?: string;
  word_id?: string;
  box?: number;
  interval_days?: number;
  easiness?: number;
  repetitions?: number;
  last_review?: string;
  next_review?: string;
  word?: RawVocabWord;
};

function getStudentId(): string {
  const id = useAuthStore.getState().user?.id;
  if (!id) throw new Error('Not authenticated: missing student id');
  return id;
}

function adaptWord(raw: RawVocabWord | undefined, wordId?: string): VocabularyWord {
  const r = raw ?? {};
  return {
    id: r.id ?? wordId ?? '',
    word: r.word ?? '',
    // Backend has a single `translation` column; surface it as RU (page prefers RU).
    translation_ru: r.translation,
    explanation: r.explanation,
    example: r.example,
    example_translation: r.example_trans,
    audio_url: r.audio_url,
    cefr_level: r.level as CEFRLevel | undefined,
    senses: Array.isArray(r.senses) ? r.senses : undefined,
    forms: r.forms,
    created_at: r.created_at,
  };
}

function adaptStudentVocab(raw: RawStudentVocab): UserVocabEntry {
  return {
    id: raw.id ?? '',
    user_id: raw.student_id ?? '',
    word_id: raw.word_id ?? '',
    word: adaptWord(raw.word ?? raw.vocabulary_word, raw.word_id),
    is_favorite: raw.is_favourite ?? false,
    is_learned: raw.is_learned ?? false,
  };
}

function adaptSRSEntry(raw: RawSRSEntry): UserVocabEntry {
  return {
    id: raw.id ?? '',
    user_id: raw.student_id ?? '',
    word_id: raw.word_id ?? '',
    word: adaptWord(raw.word, raw.word_id),
    is_favorite: false,
    is_learned: false,
    next_review_at: raw.next_review,
    review_count: raw.repetitions,
    ease_factor: raw.easiness,
  };
}

export const vocabApi = {
  async listMine(filter?: 'all' | 'favorites' | 'learned' | 'by_level', level?: CEFRLevel) {
    // Backend GET /vocab/student/words requires ?student_id= and now joins the
    // dictionary word into each entry. An optional `level` param restricts the
    // list to that CEFR level + the one below it (TZ 5.5). favorites/learned are
    // boolean flags, so we filter those client-side.
    const { data } = await api.get('/vocab/student/words', {
      params: { student_id: getStudentId(), level: filter === 'by_level' ? level : undefined },
    });
    const raw = (data?.items ?? data ?? []) as RawStudentVocab[];
    let entries = raw.map(adaptStudentVocab);
    if (filter === 'favorites') entries = entries.filter((e) => e.is_favorite);
    else if (filter === 'learned') entries = entries.filter((e) => e.is_learned);
    return entries;
  },

  /** One paginated page (server-side limit/offset). Client-side favorite/learned
   * filtering is applied by the caller on the accumulated pages. */
  /**
   * Страница личного словаря.
   *
   * ⚠️ `state` ОТБИРАЕТ НА СЕРВЕРЕ. Раньше «Избранное» и «Выучено» отсеивались
   * в браузере среди уже загруженных двадцати слов: у ученика с сорока словами
   * вкладка показывала часть, а счётчик рядом — все. Числа расходились, и это
   * читалось как пропажа слов.
   */
  async listPage(
    level: CEFRLevel | undefined,
    limit: number,
    offset: number,
    state?: 'favorites' | 'learned',
  ) {
    const { data } = await api.get('/vocab/student/words', {
      params: { student_id: getStudentId(), level, limit, offset, state },
    });
    const raw = (data?.items ?? data ?? []) as RawStudentVocab[];
    return raw.map(adaptStudentVocab);
  },

  async addToMyList(word: string, opts: { sourceBlockId?: string } = {}) {
    // Backend resolves the student from the X-User-ID header (gateway-injected),
    // but we also pass student_id explicitly for robustness. It auto-fetches the
    // translation/example via the Dictionary API. Response is a bare
    // StudentVocabulary (no joined word), so we adapt to the frontend contract.
    //
    // ⚠️ ИСТОЧНИК СЛОВА (договор vocab-word-source.md, 12.09.2026). `sourceBlockId`
    // передаёт ТОЛЬКО кнопка «в словарь» блока «набор слов»: тогда сервер берёт
    // перевод из этого задания, а не общий из справочника. Выделение в чтении и
    // ручное добавление источника не шлют. Урок не шлём: сервер берёт его по блоку.
    const { data } = await api.post('/vocab/student/words', {
      student_id: getStudentId(),
      word,
      ...(opts.sourceBlockId ? { source_block_id: opts.sourceBlockId } : {}),
    });
    return adaptStudentVocab((data ?? {}) as RawStudentVocab);
  },

  // On-demand EN→RU translation for the reading SelectionToolbar. Read-only —
  // does not add anything to the student's list.
  async translate(text: string): Promise<string> {
    const { data } = await api.post('/vocab/student/translate', { text });
    const d = (data ?? {}) as { translation?: string };
    return d.translation ?? '';
  },

  async getStats() {
    // Backend GET /vocab/student/stats requires ?student_id= and returns
    // { total, mastered, learning, new }. Map mastered->learned. The backend
    // does not provide `favorites` or `due_today`, so derive due_today from the
    // due endpoint and default favorites to 0 (backend has no favorites count).
    const studentId = getStudentId();
    const { data } = await api.get('/vocab/student/stats', { params: { student_id: studentId } });
    const raw = (data ?? {}) as Record<string, number>;

    // ⚠️ ЧИСЛА ОБЯЗАНЫ СКЛАДЫВАТЬСЯ. Раньше рядом стояли «всего», «выучено» и
    // «повтор», и они не сходились: «повтор» — это сколько пора прямо сейчас, а
    // не доля от всех. Заказчик честно спросил: «всего 7, выучено 0, повтор 5 —
    // где потерялись 2?» (06.09.2026). Теперь показываем разбор целиком:
    // всего = новые + учу + в памяти.
    //
    // ⚠️ «ПОРА ПОВТОРИТЬ» БЕРЁМ ИЗ СТАТИСТИКИ, А НЕ ИЗ ДЛИНЫ СПИСКА КАРТОЧЕК.
    // Ручка `/due` отдаёт не больше 20 штук за раз, и у ученика с шестьюдесятью
    // словами число навсегда застревало на двадцати. Заодно это на один запрос
    // меньше при каждом открытии словаря.
    return {
      total: raw.total ?? 0,
      learned: raw.mastered ?? 0,
      learning: raw.learning ?? 0,
      fresh: raw.new ?? 0,
      favorites: 0,
      due_today: raw.due ?? 0,
      modes: {
        card: raw.mode_card ?? 0,
        choose: raw.mode_choose ?? 0,
        sentence: raw.mode_sentence ?? 0,
        audio: raw.mode_audio ?? 0,
        type: raw.mode_type ?? 0,
      },
    } as VocabStats;
  },

  async getDue() {
    // Backend GET /vocab/student/due requires ?student_id= and returns []SRSEntry
    // (no joined word). Adapt SRS field names to the frontend contract.
    const { data } = await api.get('/vocab/student/due', {
      params: { student_id: getStudentId() },
    });
    const raw = (data?.items ?? data ?? []) as RawSRSEntry[];
    return raw.map(adaptSRSEntry);
  },

  /**
   * ⚠️ `mode` ОБЯЗАТЕЛЕН ПО СМЫСЛУ, ХОТЬ И НЕОБЯЗАТЕЛЕН ПО ТИПУ. У каждого
   * способа свой счёт, и слово выучено, только когда закрыты все пять. Не
   * передать способ — значит зачесть ответ в «карточку», как бы человек на
   * самом деле ни отвечал: он тренирует «на слух», а закрывается карточка, и
   * слово не станет выученным никогда. Необязательным оставлен ради старых
   * сборок приложения, которые про способы не знают.
   */
  async review(payload: {
    entry_id: string;
    quality: 0 | 1 | 2 | 3 | 4 | 5;
    sense_index?: number;
    mode?: 'card' | 'choose' | 'sentence' | 'audio' | 'type';
    /**
     * Что ученик НАБРАЛ руками — только для способов `type` и `audio`.
     *
     * ⚠️ По нему вердикт выносит сервер, а `quality` выше отбрасывается.
     * Раньше на сервер уходило только готовое «верно/неверно» из браузера, и
     * устаревшая сборка молча писала в базу неверный результат: ключи с
     * типографским апострофом («To have someone’s back») не совпадали с тем,
     * что набирают с клавиатуры (09.09.2026).
     */
    typed?: string;
  }) {
    // Backend POST /vocab/student/review expects
    // { student_id, word_id, sense_index, mode, quality, typed }.
    //
    // ⚠️ `entry_id` здесь — это id СЛОВА из справочника (`vocab.vocabulary_words`),
    // тот самый `word_id` карточки сессии, а не строка личного словаря. Имя
    // осталось от прежней сборки карточек; сервер ключуется по
    // (student_id, word_id, sense_index). `sense_index` выбирает, какое ЗНАЧЕНИЕ
    // многозначного слова засчитывается. student_id приходит из входа.
    await api.post('/vocab/student/review', {
      student_id: getStudentId(),
      word_id: payload.entry_id,
      sense_index: payload.sense_index ?? 0,
      quality: payload.quality,
      mode: payload.mode ?? 'card',
      // Пустую строку не шлём: для сервера «текста нет» означает «судить
      // нечем, вердикт клиента остаётся» — ровно то, что нужно для способов,
      // где ученик ничего не набирает.
      ...(payload.typed ? { typed: payload.typed } : {}),
    });
  },

  async toggleFavorite(entryId: string) {
    // Backend POST /vocab/student/words/:id/favourite flips the is_favourite flag
    // on the StudentVocabulary row (`entryId` is the personal-list entry id).
    // student_id is passed for scoping (and as a fallback to the header).
    await api.post(`/vocab/student/words/${entryId}/favourite`, null, {
      params: { student_id: getStudentId() },
    });
  },

  async markLearned(entryId: string, learned = true) {
    // Backend POST /vocab/student/words/:id/learned sets the is_learned flag.
    await api.post(
      `/vocab/student/words/${entryId}/learned`,
      { learned },
      { params: { student_id: getStudentId() } },
    );
  },

  async deleteWord(entryId: string) {
    // Backend DELETE /vocab/student/words/:id removes the entry from the
    // student's personal list (and its SRS scheduling row).
    await api.delete(`/vocab/student/words/${entryId}`, {
      params: { student_id: getStudentId() },
    });
  },
};

/**
 * Произношение произвольного слова тем же голосом, что и в словаре.
 *
 * Нужно для блоков урока: список слов там вводит преподаватель руками, связи
 * с записью словаря нет, и готового audio_url взять неоткуда. Сервер отдаёт
 * ссылку из кэша либо создаёт её один раз и запоминает.
 */
export async function pronounceWord(word: string): Promise<string | null> {
  try {
    const { data } = await api.get<{ audio_url?: string }>('/vocab/student/pronounce', {
      params: { word },
    });
    return (data as unknown as { audio_url?: string })?.audio_url ?? null;
  } catch {
    // Молча: вызывающий откатится на голос браузера, озвучка важнее единообразия.
    return null;
  }
}
